/* SUMMARY
 * replenishmentExportDescriptor: mapping descriptor data/csv
 * exportReplenishmentPartsToPDF: export PDF des pièces d'une demande
 * exportReplenishmentPartsToCSV: export CSV des pièces d'une demande
*/

import { formatLocalDate } from "../timezoneUtils";
import { t } from "i18next";

/**
 * Descriptor pour l'export CSV de la liste des replenishments
 */
export const replenishmentExportDescriptor: EasyTech.ExportDescriptor[] = [{
  column: "ref",
  columnTranslationKey: "replenishment:export.columns.ref",
  getter: (e: EasyTech.Replenishment) => e.ref || ""
}, {
  column: "request_from",
  columnTranslationKey: "replenishment:export.columns.request_from",
  getter: (e: EasyTech.Replenishment) =>
    e.requestFrom
      ? `${e.requestFrom.firstName} ${e.requestFrom.lastName}`
      : ""
}, {
  column: "created_by",
  columnTranslationKey: "replenishment:export.columns.created_by",
  getter: (e: EasyTech.Replenishment) => `${e.technician.firstName} ${e.technician.lastName}`
}, {
  column: "sent_to",
  columnTranslationKey: "replenishment:export.columns.sent_to",
  getter: (e: EasyTech.Replenishment) =>
    e.assignedTechnician
      ? `${e.assignedTechnician.firstName} ${e.assignedTechnician.lastName}`
      : ""
}, {
  column: "van_to_restock",
  columnTranslationKey: "replenishment:export.columns.van_to_restock",
  getter: (e: EasyTech.Replenishment) => e.van?.name || ""
}, {
  column: "status",
  columnTranslationKey: "replenishment:export.columns.status",
  getter: (e: EasyTech.Replenishment) => t(`replenishment:status.${e.status}`)
}, {
  column: "priority",
  columnTranslationKey: "replenishment:export.columns.priority",
  getter: (e: EasyTech.Replenishment) => t(`replenishment:priority.${e.priority}`)
}, {
  column: "created_on",
  columnTranslationKey: "replenishment:export.columns.created_on",
  getter: (e: EasyTech.Replenishment) => {
    const realm = localStorage.getItem("realm") || "default";
    return formatLocalDate(new Date(e.created).toISOString(), realm);
  }
}, {
  column: "updated_on",
  columnTranslationKey: "replenishment:export.columns.updated_on",
  getter: (e: EasyTech.Replenishment) => {
    const realm = localStorage.getItem("realm") || "default";
    return formatLocalDate(new Date(e.updated).toISOString(), realm);
  }
}, {
  column: "parts_count",
  columnTranslationKey: "replenishment:export.columns.parts_count",
  getter: (e: EasyTech.Replenishment) => String(e.parts.length)
}];

/**
 * Export PDF des pièces d'une demande de réapprovisionnement
 * @param replenishment - Demande de réapprovisionnement
 */
export const exportReplenishmentPartsToPDF = async (replenishment: EasyTech.Replenishment): Promise<void> => {
  // TODO: Implémenter l'export PDF
  // Utiliser une librairie comme jsPDF ou react-pdf
  console.log("Export PDF des pièces pour:", replenishment.id);
  throw new Error("Export PDF not implemented yet");
};

/**
 * Export CSV des informations d'une demande de réapprovisionnement
 * @param replenishment - Demande de réapprovisionnement
 */
export const exportReplenishmentPartsToCSV = (replenishment: EasyTech.Replenishment): void => {
  const realm = localStorage.getItem("realm") || "default";

  const headers = [
    t("replenishment:export.columns.request_from"),
    t("replenishment:export.columns.created_by"),
    t("replenishment:export.columns.sent_to"),
    t("replenishment:export.columns.van_to_restock"),
    t("replenishment:export.columns.created_on"),
    t("replenishment:export.columns.updated_on"),
    t("common:ref:label"),
    t("common:label:label"),
    t("common:quantity:label"),
    t("common:price:label"),
    t("common:unity")
  ];

  const requestFrom = replenishment.requestFrom
    ? `${replenishment.requestFrom.firstName} ${replenishment.requestFrom.lastName}`
    : "";
  const technicianFrom = `${replenishment.technician.firstName} ${replenishment.technician.lastName}`;
  const technicianTo = replenishment.assignedTechnician
    ? `${replenishment.assignedTechnician.firstName} ${replenishment.assignedTechnician.lastName}`
    : "";
  const vanTo = replenishment.van ? replenishment.van.name : "";
  const createdOn = formatLocalDate(new Date(replenishment.created).toISOString(), realm);
  const updatedOn = formatLocalDate(new Date(replenishment.updated).toISOString(), realm);

  const rows = [
    headers.join(","),
    // Première ligne avec les infos du replenishment
    [
      `"${requestFrom}"`,
      `"${technicianFrom}"`,
      `"${technicianTo}"`,
      `"${vanTo}"`,
      `"${createdOn}"`,
      `"${updatedOn}"`,
      "",
      "",
      "",
      "",
      ""
    ].join(","),
    // Lignes avec les pièces
    ...replenishment.parts.map(part => [
      "",
      "",
      "",
      "",
      "",
      "",
      `"${part.part.ref}"`,
      `"${part.part.label}"`,
      part.quantity,
      part.part.price,
      `"${part.part.unity}"`
    ].join(","))
  ];

  const csvContent = rows.join("\n");

  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);
  link.setAttribute("href", url);
  link.setAttribute("download", `replenishment-${replenishment.ref || replenishment.id}.csv`);
  link.style.visibility = "hidden";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};
